// src/pages/users/Users.tsx
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { FaKey, FaUserSecret } from "react-icons/fa";
import { FiEdit, FiTrash2, FiUserPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth as useAuthSession } from "../../auth/AuthContext";
import { useUser } from "../../auth/UserContext";
import { stashAdminSession } from "../../auth/impersonation";
import { setStoredUser } from "../../auth/session";
import { truncate } from "../../common/utils/utils";
import CreateButton from "../../components/CreateButton";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import CustomTooltip from "../../components/CustomTooltip";
import { ProfileTranslations } from "../../components/enum/ProfileTranslations";
import {
  TypeActiveStatus,
  TypeActiveStatusLabel
} from "../../components/enum/typeActiveStatusEnum";
import HelpVideoButton from "../../components/HelpVideoButton";
import UserCreateModal from "../../components/modal/UserCreateModal";
import PaginationControls from "../../components/PaginationControls";
import PageToaster from "../../components/toaster/PageToaster";

import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import { impersonateUser } from "../../services/impersonation.service";
import {
  ActionButton,
  ActionsCell,
  Container,
  StatusCell,
  Table,
  TableContainer,
  TextFeldUserSearch,
  Title,
  UserSearchCard
} from "./Users.styles";
import UserStatusSwitch from "./UserStatusSwitch";

const Users: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [userNameToDelete, setUserNameToDelete] = useState("");

  const [dialogRedefineOpen, setDialogRedefineOpen] = useState(false);
  const [selectedUserRedefine, setSelectedUserRedefine] = useState<
    number | null
  >(null);
  const [userName, setUserName] = useState<string>("");

  const [dialogRedefineAdminOpen, setDialogRedefineAdminOpen] = useState(false);
  const [adminPass, setAdminPass] = useState<string>("");

  const [search, setSearch] = useState("");
  const { user_id, user, isAdmin, isAuthChecked } = useAuth();
  const { login: setSessionToken } = useAuthSession();
  const { setUser: setSessionUser } = useUser();
  const navigate = useNavigate();

  const [impersonating, setImpersonating] = useState<number | null>(null);
  const [impersonateTarget, setImpersonateTarget] = useState<any>(null);

  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null); // Inicialize como null se 'THUNNAR' não for um valor padrão desejado sempre
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);
  const [userStatus, setUserStatus] = useState<TypeActiveStatus>(
    TypeActiveStatus.NONE
  );

  const containerId = "toaster-users";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );
  // Busca usuários
  const fetchUsers = async (currentPage: number) => {
    let current_status;
    let currentCompanyId = selectedCompany
      ? selectedCompany.company_id
      : undefined;

    if (userStatus === TypeActiveStatus.ACTIVE) {
      current_status = true;
    } else if (userStatus === TypeActiveStatus.INACTIVE) {
      current_status = false;
    } else {
      current_status = undefined;
    }

    try {
      const res = await api.get("/user", {
        params: {
          page: currentPage,
          limit: 8,
          search: search.trim() || undefined,
          showActives: current_status,
          company_id: isAdmin ? currentCompanyId : undefined
        }
      });
      setUsers(res.data.items);
      setTotalPages(res.data.meta.totalPages);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers(page);
  }, [page, search, userStatus, selectedCompany]);

  useEffect(() => {
    setSearch("");
  }, [isModalOpen]);

  useEffect(() => {
    if (isAuthChecked && !user) {
      navigate("/welcome");
    }
  }, [user, isAuthChecked]);

  // Exclusão de usuário
  const openDeleteDialog = (u: any) => {
    setUserToDelete(u.user_id);
    setUserNameToDelete(u.user_name);
    setDialogOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (userToDelete == null) return;
    try {
      await api.delete(`/user/${userToDelete}`);
      autoSuccess("Usuário deletado com sucesso!");
      fetchUsers(page);
    } catch (err: any) {
      const msg = err.response.data.message;
      autoError(msg);
    } finally {
      setDialogOpen(false);
      setUserToDelete(null);
    }
  };
  const handleCancelDelete = () => {
    setDialogOpen(false);
    setUserToDelete(null);
  };

  // Redefinir senha simples
  const openRedefineDialog = (id: number, name: string) => {
    setSelectedUserRedefine(id);
    setUserName(name);
    setDialogRedefineOpen(true);
  };
  const handleRedefinePass = async () => {
    if (selectedUserRedefine == null) return;
    try {
      await api.patch(`/user/redefine/${selectedUserRedefine}`, {});
      autoSuccess(`Senha redefinida para ${userName}!`);
      fetchUsers(page);
    } catch {
      autoError("Erro ao redefinir senha.");
    } finally {
      setDialogRedefineOpen(false);
      setSelectedUserRedefine(null);
      setUserName("");
    }
  };
  const handleCancelRedefine = () => {
    setDialogRedefineOpen(false);
    setSelectedUserRedefine(null);
    setUserName("");
  };

  // Redefinir senha ADMIN (com input)
  const openRedefineDialogAdmin = (id: number, name: string) => {
    setSelectedUserRedefine(id);
    setUserName(name);
    setDialogRedefineAdminOpen(true);
  };
  const handleCancelRedefineAdmin = () => {
    setDialogRedefineAdminOpen(false);
    setSelectedUserRedefine(null);
    setUserName("");
    setAdminPass("");
  };
  const handleRedefinePassAdmin = async () => {
    if (selectedUserRedefine == null) return;
    try {
      await api.patch(`/user/redefine-admin/${selectedUserRedefine}`, {
        password: adminPass
      });
      autoSuccess(`Senha admin redefinida para ${userName}!`);
      fetchUsers(page);
    } catch {
      autoError("Erro ao redefinir senha admin.");
    } finally {
      handleCancelRedefineAdmin();
    }
  };

  // Entrar como o usuário (só admin) -- para testar as features com a conta
  // dele sem saber a senha. Guarda a sessão atual do admin de lado antes de
  // trocar, para dar pra voltar depois.
  const openImpersonateDialog = (u: any) => {
    setImpersonateTarget(u);
  };

  const handleCancelImpersonate = () => {
    setImpersonateTarget(null);
  };

  const handleConfirmImpersonate = async () => {
    if (!impersonateTarget) return;
    const u = impersonateTarget;
    setImpersonateTarget(null);

    setImpersonating(u.user_id);
    try {
      const result = await impersonateUser(u.user_id);
      stashAdminSession();

      setStoredUser({
        user_id: result.user_id,
        name: result.name,
        profile: result.profile,
        avatar: result.avatar,
        company_id: result.company_id
      });
      setSessionToken(result.access_token);
      setSessionUser({
        user_id: result.user_id,
        name: result.name,
        profile: result.profile,
        avatar: result.avatar,
        company_id: result.company_id
      });

      window.location.href = "/welcome";
    } catch (err) {
      autoError(extractErrorMessage(err) || "Falha ao entrar como este usuário.");
      setImpersonating(null);
    }
  };

  const handleToggleStatus = async (id: number, cur: boolean) => {
    try {
      await api.patch(`/user/status/${id}`, { status: !cur });

      if (!cur) {
        autoSuccess("Usuário ativado com sucesso!");
      } else {
        autoWarn("Usuário desativado com sucesso!");
      }

      fetchUsers(page);
    } catch {
      autoError("Erro ao atualizar status.");
    }
  };

  useEffect(() => {
    const url = isAdmin ? "/company/admin" : "/company";

    setLoadingCompanies(true);
    api
      .get(url)
      .then((r) => setCompanies(r.data.items || r.data))
      .catch(console.error)
      .finally(() => setLoadingCompanies(false));
  }, [isAdmin]);

  return (
    <Container>
      <PageToaster containerId={containerId} />
      <Title>
        Lista de Usuários
        <CreateButton
          onClick={() => setIsModalOpen(true)}
          style={{ marginLeft: "14px" }}
        >
          <FiUserPlus size={18} /> Criar Usuário
        </CreateButton>
        <HelpVideoButton
          youtubeUrl="https://youtu.be/nKkl4yg72oI"
          modalTitle="Gerenciar usuários"
          sx={{
            position: "absolute",
            top: 0,
            right: 8,
            zIndex: 2
          }}
        />
      </Title>

      <UserSearchCard>
        <TextFeldUserSearch
          label="Dados de Usuário"
          variant="outlined"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        <FormControl>
          <InputLabel id="label-status">Status</InputLabel>
          <Select
            sx={{ width: "200px" }}
            labelId="label-status"
            value={userStatus}
            label="Status"
            onChange={(e) => setUserStatus(e.target.value as TypeActiveStatus)}
          >
            {(Object.values(TypeActiveStatus) as TypeActiveStatus[]).map(
              (statusCode) => (
                <MenuItem key={statusCode} value={statusCode}>
                  {TypeActiveStatusLabel[statusCode]}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>

        {isAdmin && (
          <Autocomplete
            sx={{ width: 400 }}
            options={companies}
            getOptionLabel={(opt) => opt.company_name || ""}
            value={selectedCompany}
            isOptionEqualToValue={(option, value) =>
              option?.company_id === value?.company_id
            }
            onChange={(_, v) => setSelectedCompany(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Empresa"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCompanies && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        )}
      </UserSearchCard>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Nome</th>
              {isAdmin && <th>Empresa</th>}
              {isAdmin && <th>Ultimo Acesso</th>}
              <th>E-mail</th>
              <th>Perfil</th>
              {/* <th>Criado em</th> */}
              {!isAdmin && <th>Status</th>}
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id}>
                <td>{u.user_name}</td>
                {isAdmin && <td>{truncate(u.company.company_name, 15)}</td>}
                {isAdmin && (
                  <td>
                    {u.last_login
                      ? new Date(u.last_login).toLocaleDateString("pt-BR")
                      : "Nunca"}
                  </td>
                )}
                <td>{u.user_email}</td>
                <td>
                  {ProfileTranslations[
                    u.profile.profile_name as keyof typeof ProfileTranslations
                  ] || u.profile.profile_name}
                </td>
                {/* <td>{new Date(u.created_at).toLocaleDateString("pt-BR")}</td> */}
                {!isAdmin && (
                  <StatusCell status={u.status}>
                    {u.status ? "Ativo" : "Inativo"}
                  </StatusCell>
                )}
                <ActionsCell>
                  {((u.user_id !== user_id &&
                    u.profile.profile_name !== "ADMIN") ||
                    isAdmin) && (
                    <CustomTooltip title="Alterar Status">
                      <UserStatusSwitch
                        checked={u.status}
                        onToggle={() => handleToggleStatus(u.user_id, u.status)}
                      />
                    </CustomTooltip>
                  )}

                  {isAdmin && (
                    <CustomTooltip title="Redefinir Senha Admin">
                      <ActionButton
                        onClick={() =>
                          openRedefineDialogAdmin(u.user_id, u.user_name)
                        }
                      >
                        <FaKey size={20} color="#a00" />
                      </ActionButton>
                    </CustomTooltip>
                  )}

                  {isAdmin && u.profile.profile_name !== "ADMIN" && (
                    <CustomTooltip title="Entrar como este usuário (modo teste)">
                      <span>
                        <ActionButton
                          onClick={() => openImpersonateDialog(u)}
                          disabled={impersonating !== null}
                        >
                          <FaUserSecret
                            size={20}
                            color={
                              impersonating === u.user_id ? "#bbb" : "#7c5cff"
                            }
                          />
                        </ActionButton>
                      </span>
                    </CustomTooltip>
                  )}

                  {((u.user_id !== user_id &&
                    u.profile.profile_name !== "ADMIN") ||
                    isAdmin) && (
                    <CustomTooltip title="Redefinir Senha">
                      <ActionButton
                        onClick={() =>
                          openRedefineDialog(u.user_id, u.user_name)
                        }
                      >
                        <FaKey size={20} color="#777" />
                      </ActionButton>
                    </CustomTooltip>
                  )}

                  {(u.profile.profile_name !== "ADMIN" || isAdmin) && (
                    <CustomTooltip title="Editar Usuário">
                      <ActionButton
                        onClick={() => {
                          setSelectedUser(u);
                          setIsModalOpen(true);
                        }}
                      >
                        <FiEdit size={20} color="#4caf50" />
                      </ActionButton>
                    </CustomTooltip>
                  )}

                  {((u.user_id !== user_id &&
                    u.profile.profile_name !== "ADMIN") ||
                    isAdmin) && (
                    <CustomTooltip title="Excluir Usuário">
                      <ActionButton onClick={() => openDeleteDialog(u)}>
                        <FiTrash2 size={20} color="#f44336" />
                      </ActionButton>
                    </CustomTooltip>
                  )}
                  <FiTrash2 size={35} color="#f4433600" />
                </ActionsCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      <PaginationControls
        page={page}
        totalPages={totalPages}
        setPage={setPage}
      />

      {/* Diálogo de personificação (modo teste) */}
      <Dialog open={!!impersonateTarget} onClose={handleCancelImpersonate}>
        <DialogTitle>Entrar como usuário</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Entrar como <strong>{impersonateTarget?.user_name}</strong>? Sua
            sessão de admin fica guardada e você pode voltar quando quiser.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelImpersonate} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmImpersonate}
            color="primary"
            variant="contained"
          >
            Entrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de exclusão */}
      <Dialog open={dialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir <strong>{userNameToDelete}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="primary"
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de redefinição simples */}
      <Dialog open={dialogRedefineOpen} onClose={handleCancelRedefine}>
        <DialogTitle>Confirmar Redefinição de Senha</DialogTitle>
        <DialogContent sx={{ padding: "20px" }}>
          <DialogContentText>
            Deseja mesmo redefinir a senha de <strong>{userName}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRedefine} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleRedefinePass}
            color="primary"
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de redefinição ADMIN com input */}
      <Dialog
        open={dialogRedefineAdminOpen}
        onClose={handleCancelRedefineAdmin}
      >
        <DialogTitle>Confirmar Redefinição de Senha Admin</DialogTitle>
        <DialogContent sx={{ padding: "20px" }}>
          <DialogContentText sx={{ marginBottom: 2 }}>
            Deseja mesmo redefinir a senha de <strong>{userName}</strong>?<br />
            Informe a nova senha:
          </DialogContentText>
          <TextField
            label="Nova Senha"
            type="password"
            fullWidth
            value={adminPass}
            onChange={(e) => setAdminPass(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRedefineAdmin} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleRedefinePassAdmin}
            color="primary"
            variant="contained"
            disabled={!adminPass.trim()}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de criação/edição de usuário */}
      <UserCreateModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
        }}
        onUserCreated={() => fetchUsers(page)}
        user={selectedUser}
      />
    </Container>
  );
};

export default Users;
